import {
  faBuilding,
  faFire,
  faPencilAlt,
  faPlus,
  faReply,
  faTrashAlt,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isAfter } from 'date-fns';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { OptionalExchangeRatesCurrentRequest } from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type {
  Activity,
  ActivityState,
  FcfsActivity,
  LongTermBidActivity,
  LongTermBiddingPlanActivity,
  ReverseAuctionActivity,
  SecondaryActivity,
  ShortTermBidActivity,
  ShortTermComfortBidActivity,
  StorageBidActivity,
  StorageProductCurrencyUnit,
  StorageProductRuntimeUnit,
  StorageProductType,
  StorageQuantityUnit,
  SurrenderActivity,
} from 'src/apis/monolith/types';
import type { ActivitiesRequest } from 'src/apis/monolith/use-activities';
import { useWithdrawStorageBid } from 'src/apis/monolith/use-withdraw-storage-bid';
import { ActionGroup } from 'src/components/buttons-and-actions/action-group';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { DataItems, DataList } from 'src/components/data-display/data-list';
import { Direction } from 'src/components/data-display/direction';
import { EmptyCard } from 'src/components/data-display/empty-card';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { FormatDate } from 'src/components/data-display/format-date';
import { FormatValue } from 'src/components/data-display/format-value';
import {
  StatusDisplay,
  type StatusProps,
} from 'src/components/data-display/status-display';
import { Divider } from 'src/components/dividers';
import { errorTextClasses } from 'src/components/form/error-message';
import { Link } from 'src/components/navigation/link';
import { Modal } from 'src/components/overlay/modal';
import { useScrollManager } from 'src/components/scroll-management';
import { SpinnerContainer } from 'src/components/spinner-container';
import { Hint } from 'src/components/text/hint';
import type { Successful } from 'src/hooks/use-axios';
import { useModal, useModal2 } from 'src/hooks/use-modal';
import { useNow } from 'src/hooks/use-now';
import { stringifySearchParams } from 'src/hooks/use-page-params-from-schema';
import type { SavedReferrer } from 'src/hooks/use-referrer';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { capacityLabels } from 'src/pages/reporting/capacity';
import { procedureTypeLabels } from 'src/pages/reporting/secondary-trading/overview/types';
import type { CreateComfortBidsPageParams } from 'src/pages/transport/auctions/comfort-bids/create/use-create-comfort-bids-deep-link';
import { ActivityExpandableCard } from 'src/pages/transport/auctions/components/activity-expandable-card';
import {
  AuctionStatusDisplay,
  auctionStatusVariantMap,
} from 'src/pages/transport/auctions/components/status';
import { DeleteComfortBidModal } from 'src/pages/transport/auctions/overview/delete-comfort-bid-modal';
import { EmbeddedShortTermBiddingForm } from 'src/pages/transport/auctions/overview/embedded-short-term-bidding-form';
import { WithdrawBidModal } from 'src/pages/transport/auctions/overview/withdraw-bid-modal';
import { WithdrawBidOnReverseAuctionModal } from 'src/pages/transport/auctions/overview/withdraw-bid-on-reverse-auction-modal';
import { cn } from 'src/utils/cn';

// used as a scroll anchor
export const createBidHeaderId = ({
  auctionId,
}: {
  auctionId: string | number;
}) => `bid-header-${auctionId}`;

// used as a scroll anchor
const createBidSectionId = ({
  auctionId,
  orderId,
}: {
  auctionId: string | number;
  orderId: string | number;
}) => `bid-section-${auctionId}-${orderId}`;

type ActivitiesProps = {
  activities: Successful<ActivitiesRequest>;
  referrer: SavedReferrer;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  calendarIdentifier?: string | null;
  /**
   * When a bid is withdrawn the activities are automatically refreshed.
   * E.g. this callback can be used to refresh other related data.
   */
  onWithdrawnBid?: () => void;
  emptyMessage: string;
};

export const Activities: FC<ActivitiesProps> = ({
  activities,
  referrer,
  exchangeRates,
  calendarIdentifier,
  onWithdrawnBid,
  emptyMessage,
}) => {
  const groupedActivities = getGroupedActivities(activities);
  return (
    <SpinnerContainer pending={activities.pending}>
      {groupedActivities.map((activitiesGroup) => (
        <ActivitiesGroup
          key={getActivityKey(activitiesGroup[0])}
          activities={activities}
          referrer={referrer}
          exchangeRates={exchangeRates}
          calendarIdentifier={calendarIdentifier}
          onWithdrawnBid={onWithdrawnBid}
          activitiesGroup={activitiesGroup}
        />
      ))}

      {!groupedActivities.length && <EmptyCard label={emptyMessage} />}
    </SpinnerContainer>
  );
};

/**
 * Grouped activities are an array of arrays with one or more activities.
 *
 * If there are activities with the same auctionId, they are grouped together.
 * This is only relevant for:
 * - LONG_TERM_AUCTION
 * - SHORT_TERM_AUCTION
 * - COMFORT_BID *with* auctionId
 * - REVERSE_AUCTION
 *
 * If there are activities without an auctionId, they stay as single activities.
 */
type GroupedActivities = Array<[Activity, ...Activity[]]>;

function getGroupedActivities(activities?: Successful<ActivitiesRequest>) {
  const groupedActivities: GroupedActivities = [];

  activities?.response.data.data.forEach((activity) => {
    if ('auctionId' in activity) {
      const existingGroup = groupedActivities.find(
        (group) =>
          'auctionId' in group[0] && group[0].auctionId === activity.auctionId
      );
      if (existingGroup) {
        existingGroup.push(activity);
      } else {
        groupedActivities.push([activity]);
      }
    } else {
      groupedActivities.push([activity]);
    }
  });

  return groupedActivities;
}

const ActivitiesGroup: FC<{
  activities: Successful<ActivitiesRequest>;
  referrer: SavedReferrer;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  calendarIdentifier?: string | null;
  onWithdrawnBid?: () => void;
  activitiesGroup: [Activity, ...Activity[]];
}> = ({
  referrer,
  calendarIdentifier,
  exchangeRates,
  activities,
  activitiesGroup,
  onWithdrawnBid,
}) => {
  const activityElements = activitiesGroup.map((activity) =>
    getActivityElement({
      exchangeRates,
      activity,
      onWithdrawnBid,
      activities,
      referrer,
      calendarIdentifier,
    })
  );
  return (
    <div
      className={`legacy:[&+&]:border-t legacy:[&+&]:border-legacy-brand-border`}
    >
      {isAuctionActivities(activitiesGroup) ? (
        <AuctionActivities
          activities={activities}
          referrer={referrer}
          exchangeRates={exchangeRates}
          calendarIdentifier={calendarIdentifier}
          activitiesGroup={activitiesGroup}
        >
          {activityElements}
        </AuctionActivities>
      ) : isReverseAuctionActivities(activitiesGroup) ? (
        <ReverseAuctionActivities
          referrer={referrer}
          activitiesGroup={activitiesGroup}
        >
          {activityElements}
        </ReverseAuctionActivities>
      ) : (
        activityElements
      )}
    </div>
  );
};

function getActivityKey(activity: Activity) {
  switch (activity.category) {
    case 'LONG_TERM_AUCTION':
      return `activity-long-term-bid-${activity.auctionId}-${activity.orderId}-${activity.bidId}-${activity.dealId}`;
    case 'SHORT_TERM_AUCTION':
      return `activity-short-term-bid-${activity.auctionId}-${activity.orderId}-${activity.bidId}`;
    case 'COMFORT_BID':
      return activity.auctionId
        ? `activity-long-term-bidding-plan-${activity.auctionId}-${activity.comfortBidId}`
        : `activity-short-term-comfort-bid-${activity.comfortBidId}`;
    case 'SECONDARY':
      return `${activity.proposalId}-${activity.tradeId}`;
    case 'REVERSE_AUCTION':
      return `activity-reverse-auction-${activity.auctionId}-${activity.bidId}`;
    case 'FCFS':
      return `activity-fcfs-${activity.dealId}-${activity.bidId}`;
    case 'SURRENDER':
      return `activity-surrender-${activity.surrenderId}`;
    case 'STORAGE_BID':
      return `activity-storage-bid-${activity.storageBidId}`;
  }
}

const AuctionActivities: FC<{
  activities: Successful<ActivitiesRequest>;
  referrer: SavedReferrer;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  calendarIdentifier?: string | null;
  activitiesGroup: [AuctionActivity, ...AuctionActivity[]];
  children: ReactNode;
}> = ({
  referrer,
  calendarIdentifier,
  exchangeRates,
  activities,
  activitiesGroup,
  children,
}) => {
  const [showAddBid, setShowAddBid] = useState(false);
  const { scrollTo } = useScrollManager();
  const firstActivity = activitiesGroup[0];
  const auctionId = firstActivity.auctionId.toString();
  return (
    <ActivityExpandableCard
      header={
        <AuctionActivitiesHeader
          referrer={referrer}
          calendarIdentifier={calendarIdentifier}
          firstActivity={firstActivity}
          toggleShowAddBid={() => setShowAddBid(!showAddBid)}
        />
      }
    >
      {showAddBid && (
        <Card
          mode="embedded"
          data-testid={`embedded-add-short-term-bid-${auctionId}`}
        >
          <EmbeddedShortTermBiddingForm
            exchangeRates={exchangeRates}
            auctionId={auctionId}
            onCancel={() => {
              setShowAddBid(false);
              scrollTo(`#${createBidHeaderId({ auctionId })}`);
            }}
            onSuccess={() => {
              setShowAddBid(false);
              scrollTo(`#${createBidHeaderId({ auctionId })}`);
              activities.refresh?.();
            }}
          />
        </Card>
      )}

      {children}
    </ActivityExpandableCard>
  );
};

const AuctionActivitiesHeader: FC<{
  referrer: SavedReferrer;
  calendarIdentifier?: string | null;
  firstActivity: AuctionActivity;
  toggleShowAddBid: (show: boolean) => void;
}> = ({ referrer, calendarIdentifier, firstActivity, toggleShowAddBid }) => {
  const auctionId = firstActivity.auctionId.toString();

  const search = calendarIdentifier
    ? `identifier=${calendarIdentifier}`
    : undefined;

  const addBidLink =
    ((firstActivity.category === 'COMFORT_BID' && firstActivity.auctionId) ||
      firstActivity.category === 'SHORT_TERM_AUCTION' ||
      (firstActivity.category === 'LONG_TERM_AUCTION' &&
        firstActivity.round === '0.0')) &&
    (firstActivity.auctionPhase === 'OPEN' ||
      firstActivity.auctionPhase === 'PUBLISHED')
      ? referrer.getLinkProps({
          pathname: `/transport/auctions/${firstActivity.auctionId}`,
          search,
        })
      : null;

  const reportLink = referrer.getLinkProps({
    pathname: `/reporting/auctions/details/${firstActivity.auctionId}`,
    search,
  });

  return (
    <div
      className="grid grid-cols-[1fr_auto] content-between items-center gap-xl"
      id={createBidHeaderId({ auctionId })}
    >
      <ActivityHeaderData activity={firstActivity} dateLabel="Runtime" />

      <div
        className={`
          grid grid-flow-col items-center justify-between gap-xl
          legacy:gap-[1.6rem]
        `}
      >
        {addBidLink ? (
          <Link
            data-testid={`add-bid-link-${auctionId}`}
            mode="default-underlined"
            {...addBidLink}
          >
            Auction ID {auctionId}
          </Link>
        ) : (
          <Link
            data-testid={`add-bid-link-${auctionId}`}
            mode="default-underlined"
            {...reportLink}
          >
            Auction ID {auctionId}
          </Link>
        )}
        {addBidLink && firstActivity.category !== 'SHORT_TERM_AUCTION' && (
          <Link
            data-testid={`add-bid-${auctionId}`}
            {...addBidLink}
            mode="button-secondary"
            size="small"
          >
            <FontAwesomeIcon icon={faPlus} size="sm" aria-hidden /> Add Bid
          </Link>
        )}
        {firstActivity.category === 'SHORT_TERM_AUCTION' &&
          firstActivity.auctionPhase === 'OPEN' && (
            <Button
              data-testid={`add-bid-${auctionId}`}
              onClick={(event) => {
                event.stopPropagation();
                toggleShowAddBid(true);
              }}
              mode="secondary"
              size="small"
            >
              <FontAwesomeIcon icon={faPlus} size="sm" aria-hidden /> Add Bid
            </Button>
          )}
      </div>
    </div>
  );
};

const ReverseAuctionActivities: FC<{
  referrer: SavedReferrer;
  activitiesGroup: [ReverseAuctionActivity, ...ReverseAuctionActivity[]];
  children: ReactNode;
}> = ({ referrer, activitiesGroup, children }) => {
  const firstActivity = activitiesGroup[0];
  return (
    <ActivityExpandableCard
      header={
        <ReverseAuctionActivitiesHeader
          referrer={referrer}
          firstActivity={firstActivity}
        />
      }
    >
      {children}
    </ActivityExpandableCard>
  );
};

const ReverseAuctionActivitiesHeader: FC<{
  referrer: SavedReferrer;
  firstActivity: ReverseAuctionActivity;
}> = ({ referrer, firstActivity }) => {
  const auctionId = firstActivity.auctionId.toString();

  const isFinal =
    firstActivity.state === 'COMPLETELY_WON' || firstActivity.state === 'LOST';

  const detailsLink = isFinal
    ? referrer.getLinkProps({
        pathname: `/reporting/auctions/details/${firstActivity.auctionId}`,
        search: 'reverse=true',
      })
    : { to: `/platform/#/auctions/reverse/${firstActivity.auctionId}` };

  return (
    <div
      className="grid grid-cols-[1fr_auto] content-between items-center gap-xl"
      id={createBidHeaderId({ auctionId })}
    >
      <ActivityHeaderData
        activity={firstActivity}
        dateLabel="Product Runtime"
      />

      <Link
        data-testid={`reverse-auction-link-${auctionId}`}
        mode="default-underlined"
        {...detailsLink}
      >
        Reverse Auction ID {auctionId}
      </Link>
    </div>
  );
};

function getActivityElement({
  activity,
  ...rest
}: {
  activity: Activity;
  onWithdrawnBid?: () => void;
  activities: Successful<ActivitiesRequest>;
  referrer: SavedReferrer;
  calendarIdentifier?: string | null;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
}) {
  const key = getActivityKey(activity);

  switch (activity.category) {
    case 'COMFORT_BID':
      if (activity.auctionId === undefined)
        return (
          <ComfortBidActivityElement key={key} activity={activity} {...rest} />
        );
      else
        return (
          <LongTermBiddingPlanActivityElement
            key={key}
            activity={activity}
            {...rest}
          />
        );
    case 'LONG_TERM_AUCTION':
      return (
        <LongTermBidActivityElement key={key} activity={activity} {...rest} />
      );
    case 'SHORT_TERM_AUCTION':
      return (
        <ShortTermBidActivityElement key={key} activity={activity} {...rest} />
      );
    case 'SECONDARY':
      return (
        <SecondaryTradingActivityElement
          key={key}
          activity={activity}
          {...rest}
        />
      );
    case 'REVERSE_AUCTION':
      return (
        <ReverseAuctionActivityElement
          key={key}
          activity={activity}
          {...rest}
        />
      );
    case 'FCFS':
      return <FcfsActivityElement key={key} activity={activity} {...rest} />;

    case 'SURRENDER':
      return (
        <SurrenderedActivityElement key={key} activity={activity} {...rest} />
      );
    case 'STORAGE_BID':
      return (
        <StorageBidActivityElement key={key} activity={activity} {...rest} />
      );
  }
}

function getSecondaryTradingLink(
  activity: SecondaryActivity,
  referrer: SavedReferrer
) {
  const testid =
    activity.myOwnResponse && activity.tradeId
      ? `trade-response-id-${activity.tradeId}`
      : `proposal-id-${activity.proposalId}`;

  if (!isFinalState(activity) || activity.state === 'CLOSED') {
    return (
      <Link
        mode="default-underlined"
        {...referrer.getLinkProps(
          `/transport/secondary/proposals/${activity.proposalId}`
        )}
        data-testid={testid}
      >
        {activity.myOwnResponse ? (
          <strong>
            <FontAwesomeIcon icon={faReply} aria-hidden /> Trade Response ID{' '}
            {activity.tradeId}
          </strong>
        ) : (
          <strong>Proposal ID {activity.proposalId}</strong>
        )}
      </Link>
    );
  } else {
    if (activity.myOwnResponse) {
      return (
        <Link
          mode="default-underlined"
          {...referrer.getLinkProps(
            `/reporting/secondary/trades/${activity.tradeId}`
          )}
          data-testid={testid}
        >
          <strong>
            <FontAwesomeIcon icon={faReply} aria-hidden /> Trade Response ID{' '}
            {activity.tradeId}
          </strong>
        </Link>
      );
    } else {
      return (
        <Link
          mode="default-underlined"
          {...referrer.getLinkProps(
            `/reporting/secondary/proposals/${activity.proposalId}`
          )}
          data-testid={testid}
        >
          <strong>Proposal ID {activity.proposalId}</strong>
        </Link>
      );
    }
  }
}

type SecondaryTradingActivityElementProps = {
  activity: SecondaryActivity;
  referrer: SavedReferrer;
  activities: Successful<ActivitiesRequest>;
  calendarIdentifier?: string | null;
  onWithdrawnBid?: () => void;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
};

const SecondaryTradingActivityElement: FC<
  SecondaryTradingActivityElementProps
> = ({ activity, referrer }) => {
  const isNewDesignActive = useIsNewDesignActive();

  const statusVariant = activityStatusVariantMap[activity.state];
  const status = activity.myOwnResponse
    ? secondaryTradingActivityResponseStatusLabels[activity.state]
    : secondaryTradingActivityProposalStatusLabels[activity.state];

  const proposalType =
    activity.proposalType === 'PROPOSAL_TO_SELL' ? 'Offer' : 'Request';
  const ownResponsePrefix = activity.myOwnResponse ? '' : 'Your';
  return (
    <>
      <Card
        mode={isNewDesignActive ? 'default' : 'highlight'}
        className="not-legacy:rounded-b-none"
      >
        <ActivityHeaderData activity={activity} dateLabel="Runtime" />
      </Card>

      <ActivityItem
        variant={statusVariant}
        testid={`secondary-trading-${activity.proposalId}`}
      >
        <ActiveItemAside
          activity={activity}
          label={
            <div className="grid items-center gap-md">
              {getSecondaryTradingLink(activity, referrer)}

              <small>
                {procedureTypeLabels[activity.tradingProcedure]}
                {' · '}
                {activity.transactionType === 'ASSIGNMENT'
                  ? 'Assignment'
                  : 'Transfer of Use'}
              </small>
            </div>
          }
          status={
            <StatusDisplay
              status={status}
              variant={statusVariant}
              data-testid="trade-proposal-status"
            />
          }
        />

        <Divider direction="vertical" />

        <ActivityData>
          <DataList className="w-fit">
            <strong>
              {ownResponsePrefix} {proposalType}
            </strong>
            <DataItems>
              {activity.offer.quantity && (
                <>
                  <span>
                    {activity.myOwnResponse ? 'Marketable' : 'Quantity'}
                  </span>
                  <span>
                    <FormatValue
                      value={Number(activity.offer.quantity.value.value)}
                      label={activity.offer.quantity.unit}
                    />
                  </span>
                </>
              )}
              {activity.offer.price && (
                <>
                  <span>Price</span>
                  <span>
                    <FormatValue
                      value={Number(activity.offer.price.value.value)}
                      label={activity.offer.price.unit.label}
                    />
                  </span>
                </>
              )}
            </DataItems>
          </DataList>

          {activity.response && !activity.multipleTradeResponses && (
            <DataList className="w-fit">
              <strong>{activity.myOwnResponse ? 'Your ' : ''}Response</strong>
              <DataItems>
                {activity.response.quantity && (
                  <>
                    <span>Quantity</span>
                    <span>
                      <FormatValue
                        value={Number(activity.response.quantity.value.value)}
                        label={activity.response.quantity.unit}
                      />
                    </span>
                  </>
                )}
                {activity.response.price && (
                  <>
                    <span>Price</span>
                    <span>
                      <FormatValue
                        value={Number(activity.response.price.value.value)}
                        label={activity.response.price.unit.label}
                      />
                    </span>
                  </>
                )}
              </DataItems>
            </DataList>
          )}

          {activity.multipleTradeResponses && (
            <Hint mode="default">Multiple responses have been successful.</Hint>
          )}
        </ActivityData>
      </ActivityItem>
    </>
  );
};

type ReverseAuctionActivityElementProps = {
  activity: ReverseAuctionActivity;
  referrer: SavedReferrer;
  activities: Successful<ActivitiesRequest>;
  calendarIdentifier?: string | null;
  onWithdrawnBid?: () => void;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
};

const ReverseAuctionActivityElement: FC<ReverseAuctionActivityElementProps> = ({
  activity,
  activities,
  referrer,
}) => {
  const statusVariant = activityStatusVariantMap[activity.state];
  const status = reverseAuctionActivityStatusLabels[activity.state];

  const isFinal =
    activity.state === 'COMPLETELY_WON' || activity.state === 'LOST';

  const withdrawBidModal = useModal2(
    !isFinal
      ? {
          modal: (props) => (
            <WithdrawBidOnReverseAuctionModal
              activity={activity}
              activities={activities}
              {...props}
            />
          ),
          trigger: (props) => (
            <Button
              data-testid={`withdraw-bid-${activity.bidId}`}
              aria-label="Withdraw Bid"
              {...props}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </Button>
          ),
        }
      : null
  );

  return (
    <ActivityItem
      variant={statusVariant}
      testid={`reverse-auction-${activity.auctionId}-${activity.bidId}`}
    >
      <ActiveItemAside
        activity={activity}
        label={<strong>Bid ID {activity.bidId}</strong>}
        status={
          <StatusDisplay
            status={status}
            variant={statusVariant}
            data-testid="reverse-auction-status"
          />
        }
        actions={
          withdrawBidModal && (
            <div>
              <ActionGroup mode="button-secondary" size="small">
                {withdrawBidModal.trigger}
              </ActionGroup>
              {withdrawBidModal.modal}
            </div>
          )
        }
      />

      <Divider direction="vertical" />

      <ActivityData>
        <DataList className="w-fit">
          <strong>Your Bid</strong>
          <DataItems>
            {activity.bid.quantity && (
              <>
                <span>Quantity</span>
                <span>
                  <FormatValue
                    value={Number(activity.bid.quantity.value.value)}
                    label={activity.bid.quantity.unit}
                  />
                </span>
              </>
            )}
            {activity.bid.price && (
              <>
                <span>Price</span>
                <span>
                  <FormatValue
                    value={Number(activity.bid.price.value.value)}
                    label={activity.bid.price.unit.label}
                  />
                </span>
              </>
            )}
          </DataItems>
        </DataList>

        {activity.marketed && (
          <DataList className="w-fit">
            <strong>Marketed</strong>
            <DataItems>
              <span>Quantity</span>
              <span>
                <FormatValue
                  value={Number(activity.marketed.value.value)}
                  label={activity.marketed.unit}
                />
              </span>
            </DataItems>
          </DataList>
        )}

        <YourAllocation activity={activity} />
      </ActivityData>
    </ActivityItem>
  );
};

type StorageBidActivityElementProps = {
  activity: StorageBidActivity;
  activities: Successful<ActivitiesRequest>;
};

const StorageBidActivityElement: FC<StorageBidActivityElementProps> = ({
  activity,
  activities,
}) => {
  const isNewDesignActive = useIsNewDesignActive();

  let statusVariant = activityStatusVariantMap[activity.state];
  let statusLabel = storageBidActivityStatusLabels[activity.state];

  if (activity.storageOfferState === 'PUBLISHED') {
    statusVariant = activityStatusVariantMap['PUBLISHED'];
    statusLabel = 'Published';
  } else if (activity.storageOfferState === 'OPEN') {
    statusVariant = activityStatusVariantMap['OPEN'];
    statusLabel = 'Bid is Placed';
  }

  const quantityLabel =
    storageQuantityUnitLabels[activity.storageProductQuantityUnit];
  const priceLabel = `${storageProductCurrencyLabels[activity.storageProductCurrencyUnit]}/${quantityLabel}/${storageProductRuntimeLabels[activity.storageProductRuntimeUnit]}`;

  const pendingStatus =
    activity.storageOfferState === 'OPEN' ||
    activity.storageOfferState === 'PUBLISHED';
  const canEdit =
    pendingStatus &&
    activity.storageOfferBidInRound === activity.storageOfferCurrentRound;
  const canWithdraw = pendingStatus && activity.storageOfferCurrentRound === 0;

  const withdrawBidModal = useModal2(
    canWithdraw
      ? {
          modal: (props) => (
            <WithdrawStorageBidModal
              activity={activity}
              activities={activities}
              {...props}
            />
          ),
          trigger: (props) => (
            <Button
              data-testid={`withdraw-bid-${activity.storageBidId}`}
              aria-label="Withdraw Bid"
              {...props}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </Button>
          ),
        }
      : null
  );

  return (
    <>
      <Card
        mode={isNewDesignActive ? 'default' : 'highlight'}
        className="not-legacy:rounded-b-none"
      >
        <div className="flex flex-wrap gap-x-xl">
          <strong>{activity.storageLocationName}</strong>

          <div>
            <FontAwesomeIcon icon={faBuilding} aria-hidden />{' '}
            <Link
              to={`/market-information/players/tsos/${activity.ssoRefId}`}
              mode="default-underlined"
            >
              {activity.ssoShortName}
            </Link>
          </div>

          <div>
            <FontAwesomeIcon icon={faFire} aria-hidden />{' '}
            {storageProductTypeLabels[activity.storageOfferType]}
          </div>

          <div>
            Runtime:{' '}
            <FormatDate type="date-time" value={activity.runtime.start} /> -{' '}
            <FormatDate type="date-time" value={activity.runtime.end} />
          </div>
        </div>
      </Card>

      <ActivityItem
        variant={statusVariant}
        testid={`storage-bid-activity-${activity.storageBidId}`}
      >
        <ActiveItemAside
          activity={activity}
          label={
            <Link
              mode="default-underlined"
              to={`/platform/#/storage/offers/details/${activity.storageOfferId}`}
              data-testid={`storage-offer-id-${activity.storageOfferId}`}
            >
              <strong>Storage Offer ID {activity.storageOfferId}</strong>
            </Link>
          }
          status={
            <StatusDisplay
              status={statusLabel}
              variant={statusVariant}
              data-testid="storage-bid-activity-status"
            />
          }
          actions={
            <div>
              <ActionGroup mode="button-secondary" size="small">
                {canEdit && (
                  <Link
                    data-testid={`edit-bid-${activity.storageBidId}`}
                    aria-label="Edit Bid"
                    to={`/platform/#/storage/offers/details/${activity.storageOfferId}?editBidId=${activity.storageBidId}`}
                  >
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </Link>
                )}
                {withdrawBidModal?.trigger}
              </ActionGroup>
              {withdrawBidModal?.modal}
            </div>
          }
        />

        <Divider direction="vertical" />

        <ActivityData>
          <DataList className="w-fit">
            {activity.storageOfferAllocationMethod ===
            'NO_ALLOCATION_2_BIDDING_ROUNDS' ? (
              <strong>
                Your Bid in Round {activity.storageOfferBidInRound + 1}
              </strong>
            ) : (
              <strong>Your Bid</strong>
            )}

            <DataItems>
              {activity.storageOfferAllocationMethod ===
              'NO_ALLOCATION_2_BIDDING_ROUNDS' ? (
                <>
                  <span>Quantity</span>
                  <span>
                    <FormatValue
                      value={activity.storageProductBidDto.maxQuantityValue}
                      label={quantityLabel}
                    />
                  </span>
                </>
              ) : (
                <>
                  <span>Quantity</span>
                  {activity.storageProductBidDto.minQuantityValue ===
                  activity.storageProductBidDto.maxQuantityValue ? (
                    <span>
                      <FormatValue
                        value={activity.storageProductBidDto.minQuantityValue}
                        label={quantityLabel}
                      />
                    </span>
                  ) : (
                    <span>
                      <FormatValue
                        value={activity.storageProductBidDto.minQuantityValue}
                        label={quantityLabel}
                      />
                      {' - '}
                      <FormatValue
                        value={activity.storageProductBidDto.maxQuantityValue}
                        label={quantityLabel}
                      />
                    </span>
                  )}
                </>
              )}

              <span>Price</span>
              <span>
                <FormatValue
                  value={activity.storageProductBidDto.priceAmount.value}
                  label={priceLabel}
                />
              </span>

              {activity.storageProductBidDto.storageContractType && (
                <>
                  <span>Underlying Contract</span>
                  <span>
                    {activity.storageProductBidDto.storageContractType}
                  </span>
                </>
              )}
            </DataItems>
          </DataList>

          {activity.state === 'ALLOCATIONS_ACCEPTED' &&
            activity.storageProductBidDto.productBidAllocation && (
              <DataList className="w-fit">
                <strong>Your Allocation</strong>

                <DataItems>
                  <span>Quantity</span>
                  <span>
                    <FormatValue
                      value={
                        activity.storageProductBidDto.productBidAllocation
                          .allocatedQuantityValue
                      }
                      label={quantityLabel}
                    />
                  </span>

                  {activity.storageProductBidDto.productBidAllocation
                    .priceValue && (
                    <>
                      <span>Price</span>
                      <span>
                        <FormatValue
                          value={
                            activity.storageProductBidDto.productBidAllocation
                              .priceValue.value
                          }
                          label={priceLabel}
                        />
                      </span>
                    </>
                  )}
                </DataItems>
              </DataList>
            )}
        </ActivityData>
      </ActivityItem>
    </>
  );
};

type FcfsActivityElementProps = {
  activity: FcfsActivity;
  referrer: SavedReferrer;
  activities: Successful<ActivitiesRequest>;
  calendarIdentifier?: string | null;
  onWithdrawnBid?: () => void;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
};

const FcfsActivityElement: FC<FcfsActivityElementProps> = ({
  activity,
  referrer,
}) => {
  const isNewDesignActive = useIsNewDesignActive();

  // FCFS activities currently only have the "PROCESSED" state
  const fcfsStatusVariant: StatusProps['variant'] = 'success';
  const fcfsStatus = 'Booking was Successful';

  return (
    <>
      <Card
        mode={isNewDesignActive ? 'default' : 'highlight'}
        className="not-legacy:rounded-b-none"
      >
        <ActivityHeaderData activity={activity} dateLabel="Product Runtime" />
      </Card>

      <ActivityItem
        variant={fcfsStatusVariant}
        testid={`fcfs-activity-${activity.dealId}`}
      >
        <ActiveItemAside
          activity={activity}
          label={
            <Link
              mode="default-underlined"
              {...referrer.getLinkProps(
                `/reporting/my-transactions/regular-fcfs/details-by-booking-id/${activity.dealId}`
              )}
              data-testid={`booking-id-${activity.dealId}`}
            >
              <strong>Regular FCFS Booking ID {activity.dealId}</strong>
            </Link>
          }
          status={
            <StatusDisplay
              status={fcfsStatus}
              variant={fcfsStatusVariant}
              data-testid="fcfs-activity-status"
            />
          }
        />

        <Divider direction="vertical" />

        <ActivityData>
          <DataList className="w-fit">
            <strong>Your Allocation</strong>
            <DataItems>
              {activity.allocation.quantity && (
                <>
                  <span>Quantity</span>
                  <span>
                    <FormatValue
                      value={Number(activity.allocation.quantity.value.value)}
                      label={activity.allocation.quantity.unit}
                    />
                  </span>
                </>
              )}
              {activity.allocation.price && (
                <>
                  <span>Reg. Tariff</span>
                  <span>
                    <FormatValue
                      value={Number(activity.allocation.price.value.value)}
                      label={activity.allocation.price.unit.label}
                    />
                  </span>
                </>
              )}
              {activity.undiscounted && (
                <>
                  <span>Undiscounted Tariff</span>
                  <span>Yes</span>
                </>
              )}
            </DataItems>
          </DataList>
        </ActivityData>
      </ActivityItem>
    </>
  );
};

type SurrenderedActivityElementProps = {
  activity: SurrenderActivity;
};

const SurrenderedActivityElement: FC<SurrenderedActivityElementProps> = ({
  activity,
}) => {
  const isNewDesignActive = useIsNewDesignActive();

  // surrender activities currently only have the "APPROVED" state
  const surrenderStatusVariant: StatusProps['variant'] = 'success';
  const surrenderStatus = 'Surrender was Successful';

  return (
    <>
      <Card
        mode={isNewDesignActive ? 'default' : 'highlight'}
        className="not-legacy:rounded-b-none"
      >
        <ActivityHeaderData activity={activity} dateLabel="Runtime" />
      </Card>

      <ActivityItem
        variant={surrenderStatusVariant}
        testid={`surrender-activity-${activity.surrenderId}`}
      >
        <ActiveItemAside
          activity={activity}
          label={
            <Link
              mode="default-underlined"
              to={`/platform/#/reporting/surrender/details/${activity.surrenderId}`}
              data-testid={`surrender-id-${activity.surrenderId}`}
            >
              <strong>Surrender ID {activity.surrenderId}</strong>
            </Link>
          }
          status={
            <StatusDisplay
              status={surrenderStatus}
              variant={surrenderStatusVariant}
            />
          }
        />

        <Divider direction="vertical" />

        <ActivityData>
          <DataList className="w-fit">
            <strong>Your Surrender</strong>
            <DataItems>
              {activity.offer.quantity && (
                <>
                  <span>Quantity</span>
                  <span>
                    <FormatValue
                      value={Number(activity.offer.quantity.value.value)}
                      label={activity.offer.quantity.unit}
                    />
                  </span>
                </>
              )}
            </DataItems>
          </DataList>
        </ActivityData>
      </ActivityItem>
    </>
  );
};

type ComfortBidActivityElementProps = {
  activities: Successful<ActivitiesRequest>;
  activity: ShortTermComfortBidActivity;
  referrer: SavedReferrer;
};

const ComfortBidActivityElement: FC<ComfortBidActivityElementProps> = ({
  activities,
  activity,
  referrer,
}) => {
  const isNewDesignActive = useIsNewDesignActive();

  const statusVariant = activityStatusVariantMap[activity.state];

  const deleteComfortBid = useModal2({
    modal: (props) => (
      <DeleteComfortBidModal
        activity={activity}
        activities={activities}
        {...props}
      />
    ),
    trigger: (props) => (
      <Button
        data-testid={`delete-comfort-bid-${activity.orderId}`}
        aria-label="Delete Comfort Bid"
        {...props}
      >
        <FontAwesomeIcon icon={faTrashAlt} />
      </Button>
    ),
  });

  const editLink = referrer.getLinkProps(
    `/transport/auctions/comfort-bids/edit/${activity.comfortBidId}`
  );

  return (
    <div>
      {deleteComfortBid.modal}
      <Card
        mode={isNewDesignActive ? 'default' : 'highlight'}
        className={`
          grid grid-cols-[1fr_auto] content-between items-center gap-xl
          not-legacy:rounded-b-none
        `}
      >
        <ActivityHeaderData
          activity={activity}
          dateLabel="Submission Interval"
        />

        <div
          className={`
            grid grid-flow-col items-center justify-between gap-xl
            legacy:gap-[1.6rem]
          `}
        >
          <Link
            {...referrer.getLinkProps({
              pathname: '/transport/auctions/comfort-bids/create',
              search: stringifySearchParams({
                networkPointId: activity.networkPoint.id.toString(),
              } satisfies CreateComfortBidsPageParams),
            })}
            mode="button-secondary"
            size="small"
          >
            <FontAwesomeIcon icon={faPlus} size="sm" aria-hidden /> Add Comfort
            Bid
          </Link>
        </div>
      </Card>

      <ActivityItem
        variant={statusVariant}
        testid={`activity-comfort-bid-${activity.comfortBidId}`}
      >
        <ActiveItemAside
          activity={activity}
          label={
            <Link
              mode="default-underlined"
              {...editLink}
              data-testid={`bid-id-detail-link-${activity.comfortBidId}`}
            >
              <strong>Comfort Bid ID {activity.comfortBidId}</strong>
            </Link>
          }
          status={
            <StatusDisplay
              variant={statusVariant}
              status={comfortBidActivityStatusLabels[activity.state]}
            />
          }
          actions={
            <ActionGroup mode="button-secondary" size="small">
              <Link
                data-testid={`edit-bid-${activity.orderId}`}
                aria-label="Edit Bid"
                mode="button-secondary"
                {...editLink}
              >
                <FontAwesomeIcon icon={faPencilAlt} />
              </Link>

              {deleteComfortBid.trigger}
            </ActionGroup>
          }
        />

        <Divider direction="vertical" />

        <ActivityData>
          <DataList>
            <strong>Your Comfort Bid</strong>
            <DataItems>
              {activity.bid.minimumQuantity && activity.bid.maximumQuantity && (
                <>
                  <span>Amount</span>

                  {activity.bid.minimumQuantity.value.value ===
                  activity.bid.maximumQuantity.value.value ? (
                    <div>
                      <FormatValue
                        value={Number(activity.bid.minimumQuantity.value.value)}
                        label={activity.bid.minimumQuantity.unit}
                      />
                    </div>
                  ) : (
                    <div>
                      <FormatValue
                        value={Number(activity.bid.minimumQuantity.value.value)}
                        label={activity.bid.minimumQuantity.unit}
                      />
                      {' - '}
                      <FormatValue
                        value={Number(activity.bid.maximumQuantity.value.value)}
                        label={activity.bid.maximumQuantity.unit}
                      />
                    </div>
                  )}
                </>
              )}
              {activity.bid.price && (
                <>
                  <span>Surcharge</span>
                  <span>
                    <FormatValue
                      value={Number(activity.bid.price.value.value)}
                      label={activity.bid.price.unit.label}
                    />
                  </span>
                </>
              )}
              {activity.undiscounted && (
                <>
                  <span>Undiscounted Tariff</span>
                  <span>Yes</span>
                </>
              )}
            </DataItems>
          </DataList>
        </ActivityData>
      </ActivityItem>
    </div>
  );
};

type LongTermBiddingPlanActivityElementProps = {
  activities: Successful<ActivitiesRequest>;
  activity: LongTermBiddingPlanActivity;
  referrer: SavedReferrer;
  calendarIdentifier?: string | null;
  onWithdrawnBid?: () => void;
};

const LongTermBiddingPlanActivityElement: FC<
  LongTermBiddingPlanActivityElementProps
> = ({
  activity,
  onWithdrawnBid,
  activities,
  referrer,
  calendarIdentifier,
}) => {
  return (
    <ActivityItem
      variant={auctionStatusVariantMap[activity.auctionPhase]}
      testid={`activity-comfort-bid-${activity.auctionId}-${activity.orderId}-${activity.comfortBidId}`}
    >
      <SharedAuctionData
        activity={activity}
        activities={activities}
        referrer={referrer}
        calendarIdentifier={calendarIdentifier}
        onWithdrawnBid={onWithdrawnBid}
      />

      <Divider direction="vertical" />

      <ActivityData>
        <div
          className={`
            grid grid-flow-row items-center gap-md
            lg:grid-flow-col lg:gap-xl
            legacy:gap-[1rem]
            legacy:lg:gap-[2rem]
          `}
        >
          <DataList className="w-fit">
            <strong>
              {activity.auctionPhase === 'PUBLISHED'
                ? 'Offer Round 0.0'
                : 'Offer Round'}
            </strong>
            <DataItems>
              {activity.offer?.quantity && (
                <>
                  <span>Marketable</span>
                  <span>
                    <FormatValue
                      value={Number(activity.offer.quantity.value.value)}
                      label={activity.offer.quantity.unit}
                    />
                  </span>
                </>
              )}
              {activity.startingPrice && (
                <>
                  {activity.undiscounted && (
                    <>
                      <span>Starting Price</span>
                      <span>
                        <FormatValue
                          value={
                            activity.discountStorageFactor != null
                              ? parseFloat(activity.startingPrice.value.value)
                              : parseFloat(activity.startingPrice.value.value) *
                                4
                          }
                          label={activity.startingPrice.unit.label}
                        />
                      </span>
                    </>
                  )}

                  {!activity.undiscounted && (
                    <>
                      <span>Starting Price</span>
                      <span>
                        <FormatValue
                          value={
                            activity.discountStorageFactor != null
                              ? parseFloat(activity.startingPrice.value.value) *
                                parseFloat(activity.discountStorageFactor)
                              : parseFloat(activity.startingPrice.value.value)
                          }
                          label={activity.startingPrice.unit.label}
                        />
                      </span>
                    </>
                  )}
                </>
              )}

              {activity.offer?.multiPrice && (
                <>
                  <span>Price</span>
                  <span>
                    <FormatValue
                      value={Number(activity.offer.multiPrice[0].value.value)}
                      label={activity.offer.multiPrice[0].unit.label}
                    />
                    {activity.offer.multiPrice.length === 2 && (
                      <>
                        {' '}
                        <em>or</em> <br />
                        <FormatValue
                          value={Number(
                            activity.offer.multiPrice[1].value.value
                          )}
                          label={activity.offer.multiPrice[1].unit.label}
                        />
                      </>
                    )}
                  </span>
                </>
              )}
            </DataItems>
          </DataList>
        </div>

        <DataList>
          <strong>Your Next Bid Request</strong>
          <DataItems>
            {activity.bid.maximumQuantity && (
              <>
                <span>Amount</span>
                <span>
                  <FormatValue
                    value={Number(activity.bid.maximumQuantity.value.value)}
                    label={activity.bid.maximumQuantity.unit}
                  />
                </span>
              </>
            )}
          </DataItems>
        </DataList>
      </ActivityData>
    </ActivityItem>
  );
};

type LongTermBidActivityElementProps = {
  activities: Successful<ActivitiesRequest>;
  activity: LongTermBidActivity;
  referrer: SavedReferrer;
  calendarIdentifier?: string | null;
  onWithdrawnBid?: () => void;
};

const LongTermBidActivityElement: FC<LongTermBidActivityElementProps> = ({
  activity,
  onWithdrawnBid,
  activities,
  referrer,
  calendarIdentifier,
}) => {
  // if the shipper used bid requests and the surcharge of the new round increases so much
  // that no bid request can be applied the API will send us the PREVIOUS bid.
  // this can be very misleading as it looks like the shipper has already an active bid in the new round
  // which is not true. so we need to check if the bid is outdated and display a warning.
  const outdatedBid =
    activity.auctionPhase === 'OPEN' &&
    activity.bid &&
    activity.bid.round !== activity.round;

  return (
    <ActivityItem
      variant={
        activity.state === 'LOST'
          ? 'error'
          : auctionStatusVariantMap[activity.auctionPhase]
      }
      testid={`activity-long-term-${activity.auctionId}-${activity.orderId}-${activity.bidId}-${activity.dealId ?? 'no-deal-id'}`}
    >
      <SharedAuctionData
        activity={activity}
        activities={activities}
        referrer={referrer}
        calendarIdentifier={calendarIdentifier}
        onWithdrawnBid={onWithdrawnBid}
      />

      <Divider direction="vertical" />

      <ActivityData>
        <DataList className="w-fit">
          <strong>Offer Round {activity.round}</strong>
          <DataItems>
            {activity.offer.quantity && (
              <>
                <span>Marketable</span>
                <span>
                  <FormatValue
                    value={Number(activity.offer.quantity.value.value)}
                    label={activity.offer.quantity.unit}
                  />
                </span>
              </>
            )}

            {activity.undiscounted && (
              <>
                <span>Starting Price</span>
                <span>
                  <FormatValue
                    value={
                      activity.discountStorageFactor != null
                        ? parseFloat(activity.startingPrice.value.value)
                        : parseFloat(activity.startingPrice.value.value) * 4
                    }
                    label={activity.startingPrice.unit.label}
                  />
                </span>
              </>
            )}

            {!activity.undiscounted && (
              <>
                <span>Starting Price</span>
                <span>
                  <FormatValue
                    value={
                      activity.discountStorageFactor != null
                        ? parseFloat(activity.startingPrice.value.value) *
                          parseFloat(activity.discountStorageFactor)
                        : parseFloat(activity.startingPrice.value.value)
                    }
                    label={activity.startingPrice.unit.label}
                  />
                </span>
              </>
            )}

            {activity.offer.multiPrice && (
              <>
                <span>Surcharge</span>
                <span>
                  <FormatValue
                    value={Number(activity.offer.multiPrice[0].value.value)}
                    label={activity.offer.multiPrice[0].unit.label}
                  />
                  {activity.offer.multiPrice.length === 2 && (
                    <>
                      {' '}
                      <em>or</em> <br />
                      <FormatValue
                        value={Number(activity.offer.multiPrice[1].value.value)}
                        label={activity.offer.multiPrice[1].unit.label}
                      />
                    </>
                  )}
                </span>
              </>
            )}
          </DataItems>
        </DataList>

        {outdatedBid ? (
          <div className="w-fit">
            <strong>Your Bid Round {activity.round}</strong>
            <p className={errorTextClasses}>No Bid Placed</p>
          </div>
        ) : (
          <DataList className="w-fit">
            <strong>
              Your Bid {activity.bid.round ? `Round ${activity.bid.round}` : ''}
            </strong>

            <DataItems>
              {activity.bid.quantity && (
                <>
                  <span>Amount</span>
                  <span>
                    <FormatValue
                      value={Number(activity.bid.quantity.value.value)}
                      label={activity.bid.quantity.unit}
                    />
                  </span>
                </>
              )}
            </DataItems>
          </DataList>
        )}

        <YourAllocation activity={activity} />
      </ActivityData>
    </ActivityItem>
  );
};

type ShortTermBidActivityElementProps = {
  activities: Successful<ActivitiesRequest>;
  activity: ShortTermBidActivity;
  referrer: SavedReferrer;
  calendarIdentifier?: string | null;
  onWithdrawnBid?: () => void;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
};

const ShortTermBidActivityElement: FC<ShortTermBidActivityElementProps> = ({
  activity,
  onWithdrawnBid,
  activities,
  referrer,
  calendarIdentifier,
  exchangeRates,
}) => {
  const [showEditBid, setShowEditBid] = useState(false);
  const { scrollTo } = useScrollManager();
  return (
    <>
      <ActivityItem
        variant={
          activity.state === 'LOST'
            ? 'error'
            : auctionStatusVariantMap[activity.auctionPhase]
        }
        testid={`activity-short-term-${activity.auctionId}-${activity.orderId}-${activity.bidId}`}
      >
        <SharedAuctionData
          activity={activity}
          activities={activities}
          referrer={referrer}
          calendarIdentifier={calendarIdentifier}
          onWithdrawnBid={onWithdrawnBid}
          toggleShowEditBid={() => setShowEditBid(!showEditBid)}
        />

        <Divider direction="vertical" />

        <ActivityData id={createBidSectionId(activity)}>
          <DataList className="w-fit">
            <strong>Offer</strong>
            <DataItems>
              {activity.offer.quantity && (
                <>
                  <span>Marketable</span>
                  <span>
                    <FormatValue
                      value={Number(activity.offer.quantity.value.value)}
                      label={activity.offer.quantity.unit}
                    />
                  </span>
                </>
              )}

              {activity.undiscounted && (
                <>
                  <span>Starting Price</span>
                  <span>
                    <FormatValue
                      value={
                        activity.discountStorageFactor != null
                          ? parseFloat(activity.startingPrice.value.value)
                          : parseFloat(activity.startingPrice.value.value) * 4
                      }
                      label={activity.startingPrice.unit.label}
                    />
                  </span>
                </>
              )}

              {!activity.undiscounted && (
                <>
                  <span>Starting Price</span>
                  <span>
                    <FormatValue
                      value={
                        activity.discountStorageFactor != null
                          ? parseFloat(activity.startingPrice.value.value) *
                            parseFloat(activity.discountStorageFactor)
                          : parseFloat(activity.startingPrice.value.value)
                      }
                      label={activity.startingPrice.unit.label}
                    />
                  </span>
                </>
              )}
            </DataItems>
          </DataList>

          <DataList className="w-fit">
            <strong>Your Bid</strong>
            <DataItems>
              {activity.bid.minimumQuantity && activity.bid.maximumQuantity && (
                <>
                  <span>Amount</span>

                  {activity.bid.minimumQuantity.value.value ===
                  activity.bid.maximumQuantity.value.value ? (
                    <div>
                      <FormatValue
                        value={Number(activity.bid.minimumQuantity.value.value)}
                        label={activity.bid.minimumQuantity.unit}
                      />
                    </div>
                  ) : (
                    <div>
                      <FormatValue
                        value={Number(activity.bid.minimumQuantity.value.value)}
                        label={activity.bid.minimumQuantity.unit}
                      />
                      {' - '}
                      <FormatValue
                        value={Number(activity.bid.maximumQuantity.value.value)}
                        label={activity.bid.maximumQuantity.unit}
                      />
                    </div>
                  )}
                </>
              )}
              {activity.bid.price && (
                <>
                  <span>Surcharge</span>
                  <span>
                    <FormatValue
                      value={Number(activity.bid.price.value.value)}
                      label={activity.bid.price.unit.label}
                    />
                  </span>
                </>
              )}
            </DataItems>
          </DataList>

          <YourAllocation activity={activity} />
        </ActivityData>
      </ActivityItem>

      {showEditBid && (
        <Card
          mode="embedded"
          data-testid={`embedded-edit-short-term-bid-${activity.orderId}`}
        >
          <EmbeddedShortTermBiddingForm
            exchangeRates={exchangeRates}
            auctionId={activity.auctionId.toString()}
            orderId={activity.orderId.toString()}
            onCancel={() => {
              setShowEditBid(false);
              scrollTo(`#${createBidSectionId(activity)}`);
            }}
            onSuccess={() => {
              setShowEditBid(false);
              scrollTo(`#${createBidSectionId(activity)}`);
              activities.refresh?.();
            }}
          />
        </Card>
      )}
    </>
  );
};

type SharedAuctionDataProps = {
  activities: Successful<ActivitiesRequest>;
  activity:
    | ShortTermBidActivity
    | LongTermBidActivity
    | LongTermBiddingPlanActivity;
  referrer: SavedReferrer;
  calendarIdentifier?: string | null;
  onWithdrawnBid?: () => void;
  toggleShowEditBid?: () => void;
};

const SharedAuctionData: FC<SharedAuctionDataProps> = ({
  activities,
  activity,
  referrer,
  calendarIdentifier,
  onWithdrawnBid,
  toggleShowEditBid,
}) => {
  const now = useNow();

  const hasActions =
    (activity.state === 'OPEN' || activity.state === 'IN_PROGRESS') &&
    !isInProgressButLost(activity) &&
    !denyActivityEdit(activity, now);

  const withdrawBidModal = useModal(WithdrawBidModal, {
    activity,
    activities,
    onSuccess: onWithdrawnBid,
  });

  const search = calendarIdentifier
    ? `identifier=${calendarIdentifier}`
    : undefined;

  const editLink = referrer.getLinkProps({
    pathname: `/transport/auctions/${activity.auctionId}/${activity.orderId}`,
    search,
  });

  const label =
    activity.category === 'COMFORT_BID' ? (
      <strong>Bidding Plan ID {activity.comfortBidId}</strong>
    ) : (
      <strong>Bid ID {activity.bidId}</strong>
    );

  return (
    <ActiveItemAside
      activity={activity}
      label={
        hasActions ? (
          <Link
            mode="default-underlined"
            {...editLink}
            data-testid={
              activity.category === 'COMFORT_BID'
                ? `bid-id-detail-link-${activity.comfortBidId}`
                : `bid-id-detail-link-${activity.bidId}`
            }
          >
            {label}
          </Link>
        ) : (
          label
        )
      }
      status={
        <AuctionStatusDisplay
          data-testid={`auction-status-bid-${activity.category === 'COMFORT_BID' ? activity.comfortBidId : activity.bidId}`}
          phase={activity.auctionPhase}
          runningAuctions
          activityState={activity.state}
        />
      }
      actions={
        hasActions && (
          <div>
            <ActionGroup mode="button-secondary" size="small">
              {(activity.category === 'COMFORT_BID' ||
                activity.category === 'LONG_TERM_AUCTION') && (
                <Link
                  data-testid={`edit-bid-${activity.orderId}`}
                  aria-label="Edit Bid"
                  {...editLink}
                >
                  <FontAwesomeIcon icon={faPencilAlt} />
                </Link>
              )}

              {activity.category === 'SHORT_TERM_AUCTION' && (
                <Button
                  data-testid={`edit-bid-${activity.orderId}`}
                  aria-label="Edit Bid"
                  onClick={toggleShowEditBid}
                >
                  <FontAwesomeIcon icon={faPencilAlt} />
                </Button>
              )}

              {(activity.category === 'COMFORT_BID' ||
                activity.category === 'SHORT_TERM_AUCTION' ||
                (activity.category === 'LONG_TERM_AUCTION' &&
                  permitLongTermBidDeletion(activity.round, activity.state) &&
                  activity.round === activity.bid.round)) && (
                <Button
                  data-testid={`withdraw-bid-${activity.orderId}`}
                  aria-label="Withdraw Bid"
                  onClick={withdrawBidModal.open}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </Button>
              )}
            </ActionGroup>
            {withdrawBidModal.modal}
          </div>
        )
      }
    />
  );
};

// from angular/src/main/webapp/app/_services/activities.service.ts: `frame.inProgressButLost` in `determineFrame()`
export function isInProgressButLost(
  activity:
    | LongTermBidActivity
    | ShortTermBidActivity
    | LongTermBiddingPlanActivity
) {
  if (
    activity.category === 'LONG_TERM_AUCTION' &&
    activity.state === 'IN_PROGRESS' &&
    activity.round &&
    activity.bid.round
  ) {
    // example for windows: "0.0" -> "1.0" -> "2.0" -> "1.1" -> "1.2"
    // you're not allowed to have a _gap_ (e.g. your last window was "1.0" and the current window is "1.1")
    const currentWindow = getPriceSteps(activity.round);
    const lastWindow = getPriceSteps(activity.bid.round);

    // large and small price step are equal ("2.0" and "2.0")
    if (
      currentWindow.largePriceSteps === lastWindow.largePriceSteps &&
      currentWindow.smallPriceSteps === lastWindow.smallPriceSteps
    )
      return false;

    // one large step behind ("3.0" and "2.0")
    if (
      currentWindow.largePriceSteps - 1 === lastWindow.largePriceSteps &&
      currentWindow.smallPriceSteps === 0 &&
      lastWindow.smallPriceSteps === 0
    )
      return false;

    // first small step behind ("1.1" and "2.0")
    if (
      currentWindow.largePriceSteps + 1 === lastWindow.largePriceSteps &&
      currentWindow.smallPriceSteps === 1 &&
      lastWindow.smallPriceSteps === 0
    )
      return false;

    // other small step behind ("1.2" and "1.1")
    if (
      currentWindow.largePriceSteps === lastWindow.largePriceSteps &&
      currentWindow.smallPriceSteps - 1 === lastWindow.smallPriceSteps
    )
      return false;

    // found gap
    return true;
  }

  return false;
}

function permitLongTermBidDeletion(round: string, phase: ActivityState) {
  return !(phase === 'CLOSED' && getPriceSteps(round).smallPriceSteps > 0);
}

function getPriceSteps(round: string) {
  const splitRound = round.split('.');
  return {
    largePriceSteps: parseInt(splitRound[0]),
    smallPriceSteps: parseInt(splitRound[1]),
  };
}

// from angular/src/main/webapp/app/_directives/activity.directive.ts
function denyActivityEdit(
  activity:
    | LongTermBidActivity
    | ShortTermBidActivity
    | LongTermBiddingPlanActivity,
  now: number
) {
  // deny edit when submission interval is already expired for comfort bids
  return (
    activity.category === 'COMFORT_BID' &&
    isAfter(now, new Date(activity.runtime.end))
  );
}

const statusColorMap: Record<StatusProps['variant'], string> = {
  warning: cn(`
    not-legacy:border-l-background-support-warning-pastel
    legacy:border-l-legacy-warning-light
  `),
  error: cn(`
    not-legacy:border-l-background-support-error-pastel
    legacy:border-l-legacy-error-light
  `),
  success: cn(`
    not-legacy:border-l-background-support-success-pastel
    legacy:border-l-legacy-success-light
  `),
  inactive: cn(
    `
      not-legacy:border-l-background-support-inactive-pastel
      legacy:border-l-legacy-inactive-light
    `
  ),
  ready: cn(`
    not-legacy:border-l-background-support-ready-pastel
    legacy:border-l-legacy-ready-light
  `),
};

type ActivityItemProps = {
  variant: StatusProps['variant'];
  testid: string;
  children: ReactNode;
};

const ActivityItem: FC<ActivityItemProps> = ({ variant, testid, children }) => {
  return (
    <div
      className={cn(
        `
          @container/activity-item bg-white px-[3.2rem] py-[1.6rem]
          not-legacy:border not-legacy:border-t-0 not-legacy:border-l-[2.8rem]
          not-legacy:border-border-surface-minimal
          not-legacy:last:rounded-b-xs
          legacy:border-l-[2.8rem]
          legacy:[&+&]:border-t legacy:[&+&]:border-t-legacy-brand-border
        `,
        statusColorMap[variant]
      )}
      data-testid={testid}
    >
      <div
        className={`
          grid grid-cols-1 gap-md
          @md/activity-item:grid-cols-[18rem_auto_1fr]
          @xl/activity-item:grid-cols-[28rem_auto_1fr]
        `}
      >
        {children}
      </div>
    </div>
  );
};

const ActivityData: FC<ComponentPropsWithoutRef<'div'>> = (props) => {
  return (
    <div className="@container/activity-data">
      <div
        data-testid="activity-data"
        {...props}
        className={cn(
          `
            grid grid-cols-1 items-start gap-md
            @md/activity-data:grid-cols-2
            @3xl/activity-data:grid-cols-3
          `,
          props.className
        )}
      />
    </div>
  );
};

type ActivityElementsProps = {
  activities: Successful<ActivitiesRequest>;
  referrer: SavedReferrer;
  calendarIdentifier?: string | null;
  activitiesGroup: Activity[];
  onWithdrawnBid?: () => void;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
};

export const ActivityElements: FC<ActivityElementsProps> = ({
  activities,
  referrer,
  calendarIdentifier,
  activitiesGroup,
  onWithdrawnBid,
  exchangeRates,
}) => {
  return (
    <SpinnerContainer pending={activities.pending}>
      {activitiesGroup.map((activity) =>
        getActivityElement({
          exchangeRates,
          activity,
          onWithdrawnBid,
          activities,
          referrer,
          calendarIdentifier,
        })
      )}
    </SpinnerContainer>
  );
};

type AuctionActivity =
  | LongTermBidActivity
  | ShortTermBidActivity
  | LongTermBiddingPlanActivity;

function isAuctionActivities(
  activity: [Activity, ...Activity[]]
): activity is [AuctionActivity, ...AuctionActivity[]] {
  return (
    activity[0].category === 'LONG_TERM_AUCTION' ||
    activity[0].category === 'SHORT_TERM_AUCTION' ||
    (activity[0].category === 'COMFORT_BID' &&
      activity[0].auctionId !== undefined)
  );
}

function isReverseAuctionActivities(
  activity: [Activity, ...Activity[]]
): activity is [ReverseAuctionActivity, ...ReverseAuctionActivity[]] {
  return activity[0].category === 'REVERSE_AUCTION';
}

function isFinalState(activity: Activity) {
  return (
    activity.state === 'FINISHED' ||
    activity.state === 'CANCELLED' ||
    activity.state === 'LOST' ||
    activity.state === 'PARTIALLY_WON' ||
    activity.state === 'COMPLETELY_WON' ||
    activity.state === 'CLOSED' ||
    activity.state === 'FULFILLED' ||
    activity.state === 'WITHDRAWN' ||
    activity.state === 'DELETED' ||
    activity.state === 'REJECTED' ||
    activity.state === 'EXPIRED' ||
    activity.state === 'PROCESSED' ||
    activity.state === 'PENDING'
  );
}

const ActiveItemAside: FC<{
  label: ReactNode;
  status: ReactNode;
  actions?: ReactNode;
  activity: Activity;
}> = ({ label, status, actions, activity }) => {
  return (
    <div
      className={`
        grid content-between items-center gap-0
        md:gap-xl
      `}
    >
      <div className="grid grid-flow-col items-baseline justify-between gap-md">
        <div className="grid items-center gap-md">
          {label}
          <small>{status}</small>
        </div>
        {actions}
      </div>

      <Hint mode="small">
        Submitted by {activity.createdBy} on{' '}
        <FormatDate value={activity.createdAt} type="date-time" />
      </Hint>
    </div>
  );
};

const ActivityHeaderData: FC<{
  activity: Exclude<Activity, StorageBidActivity>;
  dateLabel: string;
}> = ({ activity, dateLabel }) => {
  return (
    <div className="flex flex-wrap gap-x-xl">
      <div>
        <Link
          to={`/transport/network-points/${activity.networkPoint.id}`}
          mode="default-underlined"
        >
          <strong>{activity.networkPoint.name}</strong>
        </Link>{' '}
        <Direction networkPoint={activity.networkPoint} />
      </div>

      <div>
        <FontAwesomeIcon icon={faBuilding} aria-hidden />{' '}
        <Link
          to={`/market-information/players/tsos/${activity.networkPoint.tso0Id}`}
          mode="default-underlined"
        >
          {activity.networkPoint.tso0ShortName}
        </Link>
        {activity.networkPoint.bundled && (
          <>
            {' / '}
            <Link
              to={`/market-information/players/tsos/${activity.networkPoint.tso1Id}`}
              mode="default-underlined"
            >
              {activity.networkPoint.tso1ShortName}
            </Link>
          </>
        )}
      </div>

      {activity.capacityCategory0 && (
        <div>
          <FontAwesomeIcon icon={faFire} aria-hidden />{' '}
          {capacityLabels[activity.capacityCategory0]}
          {activity.capacityCategory1 && (
            <> / {capacityLabels[activity.capacityCategory1]}</>
          )}
        </div>
      )}

      <div>
        {dateLabel}:{' '}
        <FormatDate type="date-time" value={activity.runtime.start} /> -{' '}
        <FormatDate type="date-time" value={activity.runtime.end} />
      </div>
    </div>
  );
};

type YourAllocationProps = {
  activity: ReverseAuctionActivity | ShortTermBidActivity | LongTermBidActivity;
};

const YourAllocation: FC<YourAllocationProps> = ({ activity }) => {
  if (activity.allocation?.quantity && activity.allocation?.price) {
    return (
      <DataList className="w-fit">
        <strong>Your Allocation</strong>
        <DataItems>
          <span>Quantity</span>
          <span>
            <FormatValue
              value={Number(activity.allocation.quantity.value.value)}
              label={activity.allocation.quantity.unit}
            />
          </span>

          <span>Price</span>
          <span>
            <FormatValue
              value={Number(activity.allocation.price.value.value)}
              label={activity.allocation.price.unit.label}
            />
          </span>
        </DataItems>
      </DataList>
    );
  }

  if (activity.state === 'LOST') {
    return (
      <div>
        <strong>Your Allocation</strong>
        <p>
          <EmptyValue label="You were not allocated any capacity." />
        </p>
      </div>
    );
  }

  return null;
};

const activityStatusVariantMap: Record<ActivityState, StatusProps['variant']> =
  {
    PROCESSED: 'warning',
    WAITING: 'warning',
    CANCELLED: 'error',
    APPROVED: 'success',
    LOST: 'error',
    PARTIALLY_WON: 'success',
    OPEN: 'ready',
    NEW: 'warning',
    WITHDRAWN: 'error',
    REJECTED: 'error',
    EXPIRED: 'error',
    CLOSED: 'warning',
    FULFILLED: 'success',
    FINISHED: 'success',
    IN_PROGRESS: 'ready',
    PUBLISHED: 'warning',
    COMPLETELY_WON: 'success',
    PENDING: 'warning',
    APPROVED_EXIT: 'success',
    APPROVED_ENTRY: 'success',
    DELETED: 'error',
    NO_ALLOCATIONS: 'error',
    AWAITING_ALLOCATION: 'warning',
    ALLOCATIONS_UNCONFIRMED: 'warning',
    ALLOCATIONS_ACCEPTED: 'success',
    ALLOCATIONS_REJECTED: 'error',
    NOT_ALLOCATED: 'error',
  };

const secondaryTradingActivityProposalStatusLabels: Record<
  ActivityState,
  string | null
> = {
  PROCESSED: null,
  WAITING: null,
  CANCELLED: 'Proposal is Cancelled',
  APPROVED: null,
  LOST: null,
  PARTIALLY_WON: null,
  OPEN: 'Proposal is Open',
  NEW: 'Proposal is Drafted',
  WITHDRAWN: 'Proposal is Withdrawn',
  REJECTED: 'Proposal is Rejected',
  EXPIRED: 'Proposal is Expired',
  CLOSED: 'Proposal is Closed',
  FULFILLED: null,
  FINISHED: 'Proposal is Finished',
  IN_PROGRESS: null,
  PUBLISHED: null,
  COMPLETELY_WON: null,
  PENDING: null,
  APPROVED_EXIT: null,
  APPROVED_ENTRY: null,
  DELETED: 'Proposal is Deleted',
  NO_ALLOCATIONS: null,
  AWAITING_ALLOCATION: null,
  ALLOCATIONS_UNCONFIRMED: null,
  ALLOCATIONS_ACCEPTED: null,
  ALLOCATIONS_REJECTED: null,
  NOT_ALLOCATED: null,
};

const secondaryTradingActivityResponseStatusLabels: Record<
  ActivityState,
  string | null
> = {
  PROCESSED: null,
  WAITING: null,
  CANCELLED: 'Response is Cancelled',
  APPROVED: null,
  LOST: null,
  PARTIALLY_WON: null,
  OPEN: null,
  NEW: 'Response is Drafted',
  WITHDRAWN: 'Response is Withdrawn',
  REJECTED: 'Response is Rejected',
  EXPIRED: 'Response is Expired',
  CLOSED: null,
  FULFILLED: null,
  FINISHED: 'Response is Finished',
  IN_PROGRESS: null,
  PUBLISHED: null,
  COMPLETELY_WON: null,
  PENDING: 'Response is Pending',
  APPROVED_EXIT: null,
  APPROVED_ENTRY: null,
  DELETED: 'Response is Deleted',
  NO_ALLOCATIONS: null,
  AWAITING_ALLOCATION: null,
  ALLOCATIONS_UNCONFIRMED: null,
  ALLOCATIONS_ACCEPTED: null,
  ALLOCATIONS_REJECTED: null,
  NOT_ALLOCATED: null,
};

const reverseAuctionActivityStatusLabels: Record<ActivityState, string | null> =
  {
    PROCESSED: null,
    WAITING: null,
    CANCELLED: null,
    APPROVED: null,
    LOST: 'Bid was Unsuccessful',
    PARTIALLY_WON: null,
    OPEN: null,
    NEW: null,
    WITHDRAWN: null,
    REJECTED: null,
    EXPIRED: null,
    CLOSED: null,
    FULFILLED: null,
    FINISHED: null,
    IN_PROGRESS: 'Bid is Placed',
    PUBLISHED: null,
    COMPLETELY_WON: 'Bid was Successful',
    PENDING: null,
    APPROVED_EXIT: null,
    APPROVED_ENTRY: null,
    DELETED: null,
    NO_ALLOCATIONS: null,
    AWAITING_ALLOCATION: null,
    ALLOCATIONS_UNCONFIRMED: null,
    ALLOCATIONS_ACCEPTED: null,
    ALLOCATIONS_REJECTED: null,
    NOT_ALLOCATED: null,
  };

const comfortBidActivityStatusLabels: Record<
  ShortTermComfortBidActivity['state'],
  string
> = {
  OPEN: 'Comfort Bid is Prepared',
  CANCELLED: 'Comfort Bid is Cancelled',
};

const storageBidActivityStatusLabels: Record<
  StorageBidActivity['state'],
  string
> = {
  NO_ALLOCATIONS: 'No Allocations',
  AWAITING_ALLOCATION: 'Waiting for Allocation',
  ALLOCATIONS_UNCONFIRMED: 'Waiting for Allocation',
  ALLOCATIONS_ACCEPTED: 'Allocations Accepted',
  ALLOCATIONS_REJECTED: 'Allocations Rejected',
  NOT_ALLOCATED: 'Not Allocated',
};

const storageProductCurrencyLabels: Record<StorageProductCurrencyUnit, string> =
  {
    CENT: 'Cent',
    EURO: 'Euro',
  };

const storageQuantityUnitLabels: Record<StorageQuantityUnit, string> = {
  SBU: 'SBU',
  KWH: 'kWh',
  KWH_H: 'kWh/h',
  KWH_D: 'kWh/d',
  M3: 'm³',
  M3_H: 'm³/h',
};

const storageProductTypeLabels: Record<StorageProductType, string> = {
  SBU: 'SBU',
  STORAGE_VOLUME: 'Storage Volume',
  INJECTION_CAPACITY: 'Injection Capacity',
  WITHDRAWAL_CAPACITY: 'Withdrawal Capacity',
};

const storageProductRuntimeLabels: Record<StorageProductRuntimeUnit, string> = {
  RUNTIME: 'Runtime',
  HOUR: 'Hour',
  DAY: 'Day',
  MONTH: 'Month',
  YEAR: 'Year',
};

type WithdrawStorageBidModalProps = {
  close: () => void;
  activity: StorageBidActivity;
  activities: Successful<ActivitiesRequest>;
};

const WithdrawStorageBidModal: FC<WithdrawStorageBidModalProps> = ({
  close,
  activity,
  activities,
}) => {
  const withdrawBid = useWithdrawStorageBid();

  useEffect(() => {
    if (!withdrawBid.response) return;
    close();
    activities.refresh?.();
    // oxlint-disable-next-line exhaustive-deps
  }, [withdrawBid.response]);

  useEffect(() => {
    if (withdrawBid.response) toast.success('Bid withdrawn successfully.');
    // oxlint-disable-next-line exhaustive-deps
  }, [withdrawBid.response]);

  return (
    <Modal
      title="Withdraw Bid"
      onDismiss={close}
      footer={{
        confirmButton: {
          label: withdrawBid.pending ? 'Pending...' : 'Withdraw Bid',
          dataTestId: 'confirm-withdraw-bid',
          disabled: withdrawBid.pending,
          onClick: () => withdrawBid.execute(activity),
        },
      }}
    >
      <p>
        Please, confirm that you want to withdraw the bid on Storage Offer ID{' '}
        {activity.storageOfferId}.
      </p>
    </Modal>
  );
};
